import { useEffect, useState } from 'react';
import { formatDate, parseISO, differenceInDays } from 'date-fns';
import Panel from '../../Panel';
import { FiEye, FiEdit2 } from 'react-icons/fi';
import StatusUpdateModal from '../blocks/UpdateStatusBlock';
import NeedsAdditionalInfoModal from '../modals/needAdditionalInfo';
import { FundRequestStatus } from '~/enums/fundRequest';
import { useFormContext } from 'react-hook-form';
import ApReviewModal from '../modals/apReview';
import ForDOFApprovalModal from '../modals/forDofApproval';
import AppealedStatusModal from '../modals/appealed';
import RejectedStatusModal from '../modals/rejected';
import MarkAsPaidModal from '../modals/markAsPaid';
import GenericStatusUpdateModal from '../modals/genericStatusUpdate';
import EditSubmittedDateModal from '../modals/EditSubmittedDateModal';
import { useAuth } from '~/context/AuthProvider';
import { useFundRequestContext } from '~/context/FundRequestProvider';
import CampaignDetailsModal from './CampaignDetailsModal';
import { updateSubmittedDate } from '~/services/fund-requests';
import toast from 'react-hot-toast';

import { Link } from 'react-router-dom';
import { formatCurrency } from '~/common/form/Inputs';
// import RejectStatusHandler from '../modals/reject';

interface TitlePanelProps {
    totalExpenses?: number;
    flaggedComments: string[];
    handleSave: (data: any) => Promise<void>;
    saving: boolean;
    handleNeedsAdditionalInfoSubmit: (comment: string) => Promise<void>;
    handleApReviewSubmit: (priority: string) => Promise<void>;
    handleStatusUpdate: (
        status: FundRequestStatus,
        note: string | null
    ) => Promise<void>;
    handleAddToQueue: () => Promise<void>;
    handleSendReminder: () => Promise<void>;
    isEditable: boolean;
    reminderDate?: string | null;
    reminderType?: 'first' | 'second' | 'manual' | null;
}

const defaultStatuses = [
    { label: 'In Review', value: FundRequestStatus.IN_REVIEW },
    { label: 'Needs Additional Info', value: FundRequestStatus.NEEDS_INFO },
    { label: 'Open', value: FundRequestStatus.OPEN },
    { label: 'Pending', value: FundRequestStatus.PENDING },
    { label: 'Rejected', value: FundRequestStatus.REJECTED },
    { label: 'AP Review', value: FundRequestStatus.AP_REVIEW },
    {
        label: 'For DoF Approval',
        value: FundRequestStatus.FOR_DOF_APPROVAL,
    },
    { label: 'Appealed', value: FundRequestStatus.APPEALED },
];

const rejectApproveStatuses = ['for_dof_approval', 'appealed'];

// Map status labels to their style classes
const statusStyles: Record<string, string> = {
    'Paid': 'bg-primary',
    'Processing': 'bg-[#AFC12D]',
    'Pending': 'bg-[#F47D29]',
    'Failed': 'bg-danger text-white',
    'Submitted': 'bg-[#AFC12D]',
    'In Review': 'bg-primary',
    'Needs Additional Info': 'bg-[#952E46] text-white',
    'AP Review': 'bg-[#FEBF3E]',
    'For DoF Approval': 'bg-[#F78154]',
    'Appealed': 'bg-black text-white',
    'Rejected': 'bg-danger text-white',
    'Approved': 'bg-[#AFC12D]',
    'Resubmitted': 'bg-[#97D7E1]',
    // Default style for any unmatched status
    'default': 'bg-[#97D7E1]'
};

const TitlePanel: React.FC<TitlePanelProps> = ({
    totalExpenses,
    flaggedComments,
    handleSave,
    saving,
    handleNeedsAdditionalInfoSubmit,
    handleApReviewSubmit,
    handleStatusUpdate,
    handleAddToQueue,
    handleSendReminder,
    isEditable,
    reminderDate,
    reminderType,
}) => {
    const { user, person } = useAuth();
    const { fundRequest, fundRequestDispatch } = useFundRequestContext();
    const [modalOpen, setModalOpen] = useState(false);
    const [currentStatus, setCurrentStatus] = useState<FundRequestStatus>();
    const { handleSubmit, formState } = useFormContext();
    const { isSubmitting, isDirty } = formState;
    const [statuses, setStatuses] = useState(defaultStatuses);
    const [isAddingToQueue, setIsAddingToQueue] = useState(false);
    const [isCampaignDetailsOpen, setIsCampaignDetailsOpen] = useState(false);
    const [isSendingReminder, setIsSendingReminder] = useState(false);
    const [isSubmittedDateModalOpen, setIsSubmittedDateModalOpen] = useState(false);
    const [isSavingSubmittedDate, setIsSavingSubmittedDate] = useState(false);

    const isAssigned = fundRequest?.financial_coordinator?.user_id === user?.id;

    // Check if user has special edit roles - check multiple sources like in FundRequestDetail
    const privilegedRoles = ['AP Coordinator', 'Director of Finance'];

    // Check user roles
    const userRoles = user?.roles || [];
    const hasUserRole = userRoles.some(role => privilegedRoles.includes(role));

    // Check person groups
    const personGroups = person?.groups || [];
    const hasPersonGroup = personGroups.some(group => privilegedRoles.includes(group.name));

    // Check person roles
    const personRoles = person?.roles || [];
    const hasPersonRole = personRoles.some(role => privilegedRoles.includes(role));

    const hasSpecialEditRole = hasUserRole || hasPersonGroup || hasPersonRole;

    // Financial admins may move a request back to In Review even when they are
    // not the FC, but they do not get the full edit/status privileges above.
    const financialAdminRoles = ['Financial', 'administrator'];
    const isFinancialAdmin =
        userRoles.some(role => financialAdminRoles.includes(role)) ||
        personGroups.some(group => financialAdminRoles.includes(group.name)) ||
        personRoles.some(role => financialAdminRoles.includes(role));

    // Full status/edit rights (whole dropdown + Save). Financial admins do not
    // get this; they may only change the status, and only to In Review.
    const canEditStatus = hasSpecialEditRole || isAssigned;
    const canChangeStatus = canEditStatus || isFinancialAdmin;

    // Determine which special role to display (check all sources)
    const isDirectorOfFinance = userRoles.includes('Director of Finance') || 
                               personGroups.some(group => group.name === 'Director of Finance') ||
                               personRoles.includes('Director of Finance');
    const isAPCoordinator = userRoles.includes('AP Coordinator') || 
                           personGroups.some(group => group.name === 'AP Coordinator') ||
                           personRoles.includes('AP Coordinator');

    // Determine which special role to display (prioritize Director of Finance if they have both)
    const specialRoleName = isDirectorOfFinance ? 'Director of Finance' : isAPCoordinator ? 'AP Coordinator' : null;

    useEffect(() => {
        const availableStatuses = [...defaultStatuses];

        // Add "Mark as Paid" option for Director of Finance and AP Coordinator roles only
        if (isDirectorOfFinance || isAPCoordinator) {
            availableStatuses.push({
                label: 'Mark as Paid',
                value: FundRequestStatus.PAID,
            });
        }

        if (flaggedComments.length) {
            // Filter statuses to only the Needs Additional Info status
            setStatuses(
                availableStatuses.filter(
                    (status) => status.value === FundRequestStatus.NEEDS_INFO
                )
            );
        } else {
            // Set available statuses (includes paid option for privileged users)
            setStatuses(availableStatuses);
        }
    }, [flaggedComments, isDirectorOfFinance, isAPCoordinator]);

    const onStatusChange = (status: FundRequestStatus) => {
        setCurrentStatus(status);
        setModalOpen(true);
    };

    const handleAddToQueueClick = async () => {
        if (isAddingToQueue) return; // Prevent double-clicking

        setIsAddingToQueue(true);
        try {
            await handleAddToQueue();
        } finally {
            setIsAddingToQueue(false);
        }
    };

    const handleSendReminderClick = async () => {
        if (isSendingReminder) return; // Prevent double-clicking

        setIsSendingReminder(true);
        try {
            await handleSendReminder();
        } finally {
            setIsSendingReminder(false);
        }
    };

    const handleSaveSubmittedDate = async (newDate: string) => {
        if (!fundRequest?.id) return;

        setIsSavingSubmittedDate(true);
        try {
            const updatedFundRequest = await updateSubmittedDate(
                fundRequest.id,
                newDate
            );
            if (updatedFundRequest.detail) {
                toast.error(updatedFundRequest.detail);
                return;
            }
            fundRequestDispatch({
                type: 'set_fund_request',
                payload: updatedFundRequest,
            });
            toast.success('Submitted date updated successfully');
            setIsSubmittedDateModalOpen(false);
        } catch (error) {
            console.error('Error updating submitted date:', error);
            toast.error('Failed to update submitted date');
        } finally {
            setIsSavingSubmittedDate(false);
        }
    };

    const renderModal = (status: FundRequestStatus) => {
        switch (status) {
            case FundRequestStatus.NEEDS_INFO:
                return (
                    <NeedsAdditionalInfoModal
                        modalOpen={modalOpen}
                        setModalOpen={setModalOpen}
                        flaggedComments={flaggedComments}
                        handleNeedsAdditionalInfoSubmit={
                            handleNeedsAdditionalInfoSubmit
                        }
                    />
                );

            case FundRequestStatus.AP_REVIEW:
                return (
                    <ApReviewModal
                        modalOpen={modalOpen}
                        setModalOpen={setModalOpen}
                        handleApReviewSubmit={handleApReviewSubmit}
                    />
                );

            case FundRequestStatus.FOR_DOF_APPROVAL:
                return (
                    <ForDOFApprovalModal
                        modalOpen={modalOpen}
                        setModalOpen={setModalOpen}
                        handleStatusUpdate={handleStatusUpdate}
                    />
                );

            case FundRequestStatus.APPEALED:
                return (
                    <AppealedStatusModal
                        modalOpen={modalOpen}
                        setModalOpen={setModalOpen}
                        handleStatusUpdate={handleStatusUpdate}
                    />
                );
            case FundRequestStatus.REJECTED:
                return (
                    <RejectedStatusModal
                        modalOpen={modalOpen}
                        setModalOpen={setModalOpen}
                        handleStatusUpdate={handleStatusUpdate}
                    />
                );

            case FundRequestStatus.PAID:
                return (
                    <MarkAsPaidModal
                        modalOpen={modalOpen}
                        setModalOpen={setModalOpen}
                        handleStatusUpdate={handleStatusUpdate}
                    />
                );

            default:
                // For statuses without a specific modal (like PENDING, OPEN, etc.)
                // Use a generic status update modal
                const statusLabel = statuses.find(s => s.value === status)?.label || 'Unknown';
                return (
                    <GenericStatusUpdateModal
                        modalOpen={modalOpen}
                        setModalOpen={setModalOpen}
                        handleStatusUpdate={handleStatusUpdate}
                        status={status}
                        statusLabel={statusLabel}
                    />
                );
        }
    };

    const excludeStatuses = ['open'];
    const request_amount = fundRequest?.request_amount;
    const ntaf_id = fundRequest?.fund_info?.patient?.cf_patient_ntaf_id;
    const fund_name = fundRequest?.fund_info?.fund_name;
    const displayName = ntaf_id || fund_name || 'Unknown';
    const titleHeader = `${displayName} requests ${formatCurrency(request_amount)}`;
    const statusLabel = fundRequest?.fund_request_status?.label;
    const statusValue = fundRequest?.fund_request_status?.value;

    // Determine if Send Reminder button should be shown
    const showSendReminderButton = (() => {
        if (statusValue !== 'needs_info') return false;
        
        const now = new Date();
        
        // Case 1: It's been 5+ days past the second reminder
        if (fundRequest?.needs_info_second_reminder_sent_at) {
            const secondReminderDate = parseISO(fundRequest.needs_info_second_reminder_sent_at);
            const daysSinceSecondReminder = differenceInDays(now, secondReminderDate);
            if (daysSinceSecondReminder >= 5) return true;
        }
        
        // Case 2: It's been 5+ days since updated_date and no first reminder has been sent
        if (!fundRequest?.needs_info_reminder_sent_at && fundRequest?.updated_date) {
            const updatedDate = parseISO(fundRequest.updated_date);
            const daysSinceUpdate = differenceInDays(now, updatedDate);
            if (daysSinceUpdate >= 5) return true;
        }
        
        return false;
    })();

    return (
        <div className='mb-7.5'>
            <Panel>
                <div>
                    <div className='mb-6 flex gap-[16px] xl:mb-0 xl:flex-1'>
                        <div className='xl:flex-1'>
                            <div className='flex items-center gap-3'>
                                <h1 className='text-[38px]/[44px] font-medium'>
                                    {titleHeader}
                                </h1>
                                {/* Special Role Indicator */}
                                {hasSpecialEditRole && !isAssigned && (
                                    <div className='inline-flex items-center rounded-md bg-blue-50 px-3 py-1.5 text-sm font-medium text-blue-700 ring-1 ring-inset ring-blue-700/10'>
                                        <FiEye className='mr-2 h-4 w-4' />
                                        Editing as {specialRoleName}
                                    </div>
                                )}
                                {/* Buttons moved to title row */}
                                <div className='ml-auto flex items-center gap-3'>
                                    <Link
                                        to={`/campaigns/${fundRequest?.campaign}`}
                                        className='rounded-full border-[2px] border-primary bg-primary px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase text-white transition-colors duration-200 hover:opacity-80'
                                    >
                                        Campaign
                                    </Link>
                                    {!rejectApproveStatuses.includes(statusValue) &&
                                        statusLabel !== 'Submitted' &&
                                        canChangeStatus &&
                                        (hasSpecialEditRole || (!['in_mip', 'paid'].includes(statusValue))) && (
                                            <>
                                                <StatusUpdateModal
                                                    onStatusChange={onStatusChange}
                                                    statuses={
                                                        canEditStatus
                                                            ? statuses.filter(
                                                                  (x) =>
                                                                      !excludeStatuses.includes(
                                                                          x.value
                                                                      )
                                                              )
                                                            : [
                                                                  {
                                                                      label: 'In Review',
                                                                      value: FundRequestStatus.IN_REVIEW,
                                                                  },
                                                              ]
                                                    }
                                                    currentStatus={statusValue}
                                                />
                                                {canEditStatus && (
                                                    <button
                                                        className={`rounded-full border-[2px] px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase text-black transition-colors duration-500 hover:border-[#e6f7f8] hover:bg-[#e6f7f8] ${statusLabel === 'AP Review'
                                                            ? ''
                                                            : 'border-warning bg-warning'
                                                            } ${isSubmitting ||
                                                                !isDirty ||
                                                                saving ||
                                                                !isEditable
                                                                ? 'cursor-not-allowed opacity-50'
                                                                : ''
                                                            }`}
                                                        onClick={() =>
                                                            handleSubmit(handleSave)()
                                                        }
                                                        disabled={
                                                            isSubmitting ||
                                                            !isDirty ||
                                                            saving ||
                                                            !isEditable
                                                        }
                                                    >
                                                        {isSubmitting ? `Saving...` : `Save`}
                                                    </button>
                                                )}
                                            </>
                                        )}

                                    {statusLabel === 'Submitted' &&
                                        (hasSpecialEditRole || isAssigned) &&
                                        (hasSpecialEditRole || (!['in_mip', 'paid'].includes(statusValue))) && (
                                            <button
                                                className='rounded-full border-[2px] px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase transition-colors duration-200 hover:border-[#e6f7f8] hover:bg-[#e6f7f8]'
                                                onClick={async () => {
                                                    await handleStatusUpdate(
                                                        FundRequestStatus.IN_REVIEW,
                                                        null
                                                    );
                                                }}
                                                disabled={isSubmitting || saving}
                                            >
                                                {isSubmitting || saving
                                                    ? `Saving...`
                                                    : `Begin Review`}
                                            </button>
                                        )}

                                    {statusLabel === 'AP Review' &&
                                        (hasSpecialEditRole || isAssigned) &&
                                        (hasSpecialEditRole || (!['in_mip', 'paid'].includes(statusValue))) && (
                                            <button
                                                className={`rounded-full border-[2px] border-warning bg-warning px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase text-black transition-colors duration-500 hover:border-[#e6f7f8] hover:bg-[#e6f7f8] ${saving || isAddingToQueue ? 'cursor-not-allowed opacity-50' : ''
                                                    }`}
                                                onClick={handleAddToQueueClick}
                                                disabled={saving || isAddingToQueue}
                                            >
                                                {saving || isAddingToQueue
                                                    ? `Adding to Queue...`
                                                    : `Add to Queue (${totalExpenses})`}
                                            </button>
                                        )}

                                    {rejectApproveStatuses.includes(statusValue) &&
                                        (hasSpecialEditRole || isAssigned) &&
                                        (hasSpecialEditRole || (!['in_mip', 'paid'].includes(statusValue))) && (
                                            <>
                                                <button
                                                    className='rounded-full border-[2px] border-danger bg-danger px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase text-white transition-colors duration-500 hover:border-[#e6f7f8] hover:bg-[#e6f7f8] hover:text-black'
                                                    onClick={() => setModalOpen(true)}
                                                >
                                                    Reject
                                                </button>
                                                <button
                                                    className='rounded-full border-[2px] border-warning bg-warning px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase text-black transition-colors duration-500 hover:border-[#e6f7f8] hover:bg-[#e6f7f8]'
                                                    onClick={() =>
                                                        handleStatusUpdate(
                                                            FundRequestStatus.AP_REVIEW,
                                                            null
                                                        )
                                                    }
                                                    disabled={saving}
                                                >
                                                    {saving ? `Approving...` : `Approve`}
                                                </button>
                                                <RejectedStatusModal
                                                    modalOpen={modalOpen}
                                                    setModalOpen={setModalOpen}
                                                    handleStatusUpdate={handleStatusUpdate}
                                                />
                                            </>
                                        )}

                                    {!hasSpecialEditRole &&
                                        !isFinancialAdmin &&
                                        isAssigned === false && (
                                            <div className='text-[#949599] max-w-70'>
                                                You cannot make any changes unless you are
                                                assigned to this request.
                                            </div>
                                        )}
                                </div>
                            </div>
                            <div className='flex flex-col gap-[16px] pt-1.5 xl:flex-row xl:items-start xl:justify-between'>
                                {/* Left side - Status and info */}
                                <div className='flex flex-col gap-2'>
                                    {/* First line: Status badge, See Details, and NTAF ID */}
                                    <div className='flex flex-wrap items-center gap-[16px]'>
                                        <div
                                            className={`inline-flex rounded-full px-2 pt-0.5 pb-1 text-sm ${statusLabel && statusStyles[statusLabel]
                                                ? statusStyles[statusLabel]
                                                : statusStyles['default']
                                                }`}
                                        >
                                            {statusLabel ?? 'Unknown'}
                                        </div>
                                        <div className='text-[16px]/[24px] text-[#949599]'>
                                            {ntaf_id}
                                        </div>
                                        <button
                                            type='button'
                                            className='py-[7px] text-[14px]/[18px] font-bold text-primary hover:opacity-80'
                                            onClick={() => setIsCampaignDetailsOpen(true)}
                                        >
                                            See Details
                                        </button>
                                    </div>
                                    {/* Second line: Requested by, Created by, Submitted by, Last Updated, and Date Submitted */}
                                    <div className='flex flex-wrap items-center gap-[16px] text-[16px]/[24px] text-[#949599]'>
                                        {fundRequest?.campaign_contact && (
                                            <span>
                                                Requested by:{' '}
                                                {`${fundRequest.campaign_contact.first_name || ''} ${fundRequest.campaign_contact.last_name || ''}`.trim() || ''}
                                            </span>
                                        )}
                                        <span>
                                            Created by:{' '}
                                            {fundRequest?.created_by?.name || ''}
                                        </span>
                                        {fundRequest?.submitted_by?.name && (
                                            <span>
                                                Submitted by:{' '}
                                                {fundRequest.submitted_by.name}
                                            </span>
                                        )}
                                        <span>
                                            Last Updated:{' '}
                                            {fundRequest?.updated_date &&
                                                formatDate(
                                                    parseISO(fundRequest?.updated_date),
                                                    'MM/dd/yy'
                                                )}
                                        </span>
                                        {fundRequest?.submitted_date && (
                                            (hasSpecialEditRole || isAssigned) && fundRequest?.created_by?.is_staff ? (
                                                <button
                                                    type='button'
                                                    className='inline-flex items-center gap-1 text-primary hover:opacity-80'
                                                    onClick={() => setIsSubmittedDateModalOpen(true)}
                                                    title='Edit submitted date'
                                                >
                                                    Date Submitted:{' '}
                                                    {formatDate(
                                                        parseISO(fundRequest.submitted_date),
                                                        'MM/dd/yy'
                                                    )}
                                                    <FiEdit2 className='h-3.5 w-3.5' />
                                                </button>
                                            ) : (
                                                <span className='inline-flex items-center gap-1 text-primary'>
                                                    Date Submitted:{' '}
                                                    {formatDate(
                                                        parseISO(fundRequest.submitted_date),
                                                        'MM/dd/yy'
                                                    )}
                                                </span>
                                            )
                                        )}
                                        {!fundRequest?.submitted_date && (hasSpecialEditRole || isAssigned) && fundRequest?.created_by?.is_staff && (
                                            <button
                                                type='button'
                                                className='inline-flex items-center gap-1 text-primary hover:text-primary/80'
                                                onClick={() => setIsSubmittedDateModalOpen(true)}
                                            >
                                                <span>Add Submitted Date</span>
                                                <FiEdit2 className='h-3.5 w-3.5' />
                                            </button>
                                        )}
                                    </div>
                                </div>
                                
                                {/* Right side - Reminder (desktop only, right-aligned) */}
                                <div className='flex flex-col items-start xl:items-end gap-2'>
                                    {reminderDate && reminderType && (
                                        <div className='text-[14px]/[20px] text-[#F59E0B] font-medium'>
                                            {reminderType === 'manual'
                                                ? 'Last Reminder Sent: '
                                                : reminderType === 'second'
                                                  ? 'Second Reminder Sent: '
                                                  : 'Reminder Sent: '}
                                            {formatDate(parseISO(reminderDate), 'MM/dd/yy')}
                                        </div>
                                    )}
                                    {showSendReminderButton && (hasSpecialEditRole || isAssigned) && (
                                        <button
                                            className={`rounded-full border-[2px] border-[#F59E0B] bg-[#F59E0B] px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase text-black transition-colors duration-200 hover:border-[#e6f7f8] hover:bg-[#e6f7f8] ${isSendingReminder ? 'cursor-not-allowed opacity-50' : ''}`}
                                            onClick={handleSendReminderClick}
                                            disabled={isSendingReminder}
                                        >
                                            {isSendingReminder ? 'Sending...' : 'Send Reminder'}
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Panel>
            <CampaignDetailsModal
                open={isCampaignDetailsOpen}
                onClose={() => setIsCampaignDetailsOpen(false)}
                fundInfo={fundRequest?.fund_info}
                fundRequestId={fundRequest?.id}
            />
            <EditSubmittedDateModal
                isOpen={isSubmittedDateModalOpen}
                onClose={() => setIsSubmittedDateModalOpen(false)}
                onSave={handleSaveSubmittedDate}
                currentDate={fundRequest?.submitted_date || null}
                saving={isSavingSubmittedDate}
            />
            {currentStatus && renderModal(currentStatus)}
        </div>
    );
};

export default TitlePanel;
